// Copyright (c) Docugami, Inc. All rights reserved.

import { getTextForDgml } from "components/assisted_authoring/ClauseCardlet";
import { createFolderId } from "components/file_management/file_helpers";
import { Clause } from "networking/assistedAuthoringApi";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useSaveClauseMutation } from "store/assistedauthinfo/assistedAuthoringSlice";
import { useAaDgWordCanvasProxy } from "./aaDgWordCanvasProxy";

const textStrCharacterLimit = 100000;

interface ISaveClauseCommand {
    label: string;
    setLabel: (value: string) => void;
    isValidContent: boolean;
    canSaveClause: boolean;
    saveClause: () => Promise<void>;
    disabledMessage?: string;
    data?: Clause;
}

interface ClauseState {
    label: string;
    dgmlContentLength: number;
}

export function useSaveClauseCommand(documentId: string): ISaveClauseCommand {
    const [clauseState, setClauseState] = useState<ClauseState>({
        label: '',
        dgmlContentLength: 0
    });

    const dgWordCanvasProxy = useAaDgWordCanvasProxy(documentId);
    const [ triggerSaveClause, data ] = useSaveClauseMutation();

    const setLabel = useCallback((label: string) => setClauseState(v => ({...v, label: label})), []);

    const validateContentLength = useMemo(() => !!clauseState.dgmlContentLength && clauseState.dgmlContentLength < textStrCharacterLimit, [clauseState.dgmlContentLength]);
    const validateLabel = useMemo(() => !!clauseState.label, [clauseState.label]);
    const canSaveClause = useMemo(() => !!dgWordCanvasProxy?.initialized && validateLabel && validateContentLength, [dgWordCanvasProxy, validateLabel, validateContentLength]);

    useEffect(() => {
        if (!dgWordCanvasProxy) {
            return;
        }

        // If there is text selected initially, track selected content length in state
        dgWordCanvasProxy.getWordCanvasSelectionAsDgml().then(selectedDgml => {
            if (!!selectedDgml) {
                const selectedDgmlText = getTextForDgml(selectedDgml);
                setClauseState(v => !!v.dgmlContentLength ? v : ({...v, dgmlContentLength: selectedDgmlText.length}))
            }
        });

        function onSelectionChanged(dgEventArgs: any) {
            setClauseState(v => ({...v, dgmlContentLength: dgEventArgs.SelectionLength}));
        }

        dgWordCanvasProxy.onSelection.on(onSelectionChanged);
        return () => dgWordCanvasProxy.onSelection.off(onSelectionChanged);
    }, [dgWordCanvasProxy])

    const saveClause = useCallback(async () => {
        if (!dgWordCanvasProxy || !canSaveClause) {
            return;
        }

        const selectedDgml = await dgWordCanvasProxy.getWordCanvasSelectionAsDgml();
        const selectedDgmlText = getTextForDgml(selectedDgml ?? '');
        if (!selectedDgmlText?.length || selectedDgmlText.length > textStrCharacterLimit) {
            return;
        }

        const requestBody = {
            docSetId: createFolderId(documentId).clusterId,
            folderId: documentId,
            label: clauseState.label,
            dgmlContent: selectedDgml!
        }

        triggerSaveClause(requestBody);
    }, [canSaveClause, dgWordCanvasProxy, triggerSaveClause, documentId, clauseState])

    const disabledMessage = useMemo(() => {
        if (canSaveClause) {
            return;
        }

        if (!validateContentLength) {
            return !clauseState.dgmlContentLength ? 'Select content from your document to add as a clause to the library' : 'Content length exceeds max content length allowed. Please choose a smaller selection';
        } else if (!validateLabel) {
            return !clauseState.label ? 'Please enter a label to save this clause' : 'Label is invalid. Please enter a valid label.';
        }
    }, [validateContentLength, validateLabel, canSaveClause, clauseState])

    return {
        label: clauseState.label,
        setLabel: setLabel,
        isValidContent: validateContentLength,
        canSaveClause: canSaveClause,
        saveClause: saveClause,
        disabledMessage: disabledMessage,
        data: data?.data
    }
}